"use client"

import { ArrowUp, ChevronDown, FileText, Globe, Handshake } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { PromptInput, PromptInputActions, PromptInputTextarea } from "@/components/ui/prompt-input"
import { ChatContextSelector } from "@/components/ui/chat-context-selector"
import { ChatContextTags } from "@/components/ui/chat-context-tags"
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select"
import { cn } from "@/lib/utils"
import type { ChatContextItem, ChatScope } from "@/types/chat.types"

const SCOPE_ICONS = {
  Global: Globe,
  Deal: Handshake,
  Document: FileText,
} as const

interface AppChatComposerProps {
  draft: string
  onDraftChange: (value: string) => void
  onSubmit: () => void
  isLoading: boolean
  scope: ChatScope
  onScopeChange: (scope: ChatScope) => void
  contextItems: ChatContextItem[]
  onContextItemsChange: (items: ChatContextItem[]) => void
  onRemoveContextItem: (id: string) => void
  extraContextItems?: ChatContextItem[]
  placeholder?: string
  variant?: "panel" | "page" | "home"
  className?: string
}

export function AppChatComposer({
  draft,
  onDraftChange,
  onSubmit,
  isLoading,
  scope,
  onScopeChange,
  contextItems,
  onContextItemsChange,
  onRemoveContextItem,
  extraContextItems = [],
  placeholder = "Ask anything...",
  variant = "panel",
  className,
}: AppChatComposerProps) {
  const ScopeIcon = SCOPE_ICONS[scope]

  const isHome = variant === "home"

  return (
    <div className={cn(
      variant === "panel"
        ? "shrink-0 rounded-[inherit] border-t border-border bg-muted/60 p-2"
        : isHome
          ? ""
          : "border-t border-border/50 bg-background",
      className,
    )}>
      <div className={cn(variant === "page" && "mx-auto max-w-2xl px-6 py-4")}>
        {!isHome && <ChatContextTags items={contextItems} onRemove={onRemoveContextItem} />}
        <PromptInput
          value={draft}
          onValueChange={onDraftChange}
          onSubmit={onSubmit}
          maxHeight={isHome ? 200 : variant === "page" ? 160 : 96}
          disabled={isLoading}
          className={cn(
            isHome
              ? "rounded-[1.75rem] border border-border bg-background p-4 shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
              : "border-none bg-transparent p-0 shadow-none",
            !isHome && variant === "page" && "rounded-2xl bg-muted/50 p-3 shadow-xs",
            !isHome && variant === "panel" && "rounded-xl px-1",
          )}
        >
          <PromptInputTextarea
            className={cn(
              "min-h-[56px] px-0 py-1 text-[15px] text-foreground placeholder:text-muted-foreground/60",
              isHome && "min-h-[72px]",
              variant === "page" && !isHome && "min-h-[80px] py-3 text-sm",
              variant === "panel" && "py-3 text-sm",
            )}
            placeholder={placeholder}
          />
          <PromptInputActions className={cn(
            "items-center justify-between px-0 pb-0",
            isHome && "pt-2",
          )}>
            <div className="flex items-center gap-1.5">
              <ChatContextSelector
                value={contextItems}
                onChange={(items) => onContextItemsChange(items)}
                extraItems={extraContextItems}
              />

              <Select value={scope} onValueChange={(value) => onScopeChange(value as ChatScope)}>
                <SelectTrigger
                  size="sm"
                  className={cn(
                    "h-7 min-w-0 w-auto gap-1 px-2 py-0 text-xs shadow-none",
                    isHome
                      ? "rounded-full border-0 bg-transparent text-muted-foreground hover:bg-muted/50"
                      : "rounded-lg border bg-muted/50 hover:bg-muted",
                  )}
                >
                  <ScopeIcon className="size-3" />
                  <SelectValue />
                  {isHome && <ChevronDown className="size-3 opacity-50" />}
                </SelectTrigger>
                <SelectPopup>
                  {Object.keys(SCOPE_ICONS).map((scopeKey) => {
                    const Icon = SCOPE_ICONS[scopeKey as keyof typeof SCOPE_ICONS]
                    return (
                      <SelectItem key={scopeKey} value={scopeKey}>
                        <span className="flex items-center gap-1.5">
                          <Icon className="size-3" />
                          {scopeKey}
                        </span>
                      </SelectItem>
                    )
                  })}
                </SelectPopup>
              </Select>
            </div>

            <Button
              variant={isHome ? "ari" : undefined}
              rounded="full"
              size={isHome ? "icon-sm" : "sm"}
              className={cn(
                "border-none text-white",
                isHome
                  ? "size-8 shadow-sm hover:brightness-105"
                  : "size-9",
              )}
              style={isHome ? undefined : { background: "var(--gradient-chat-action)" }}
              onClick={onSubmit}
              disabled={!draft.trim() || isLoading}
            >
              <ArrowUp className={cn(isHome ? "size-4" : "size-5")} />
            </Button>
          </PromptInputActions>
        </PromptInput>
        {isHome && contextItems.length > 0 && (
          <div className="mt-2 px-1">
            <ChatContextTags items={contextItems} onRemove={onRemoveContextItem} />
          </div>
        )}
      </div>
    </div>
  )
}
